import * as React from "react";
import { useRoute } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ArrowLeft, MapPin } from "lucide-react";
import { transferPlanService } from "@/services/transferPlanService";
import { toast } from "sonner";
import { Link } from "wouter";

interface LocationAggregation {
  cities: Array<{
    province: string;
    kabkot: string;
    count: number;
    totalAmount: number;
    totalWimaxIncome: number;
    totalOrpusIncome: number;
    totalOrdaIncome: number;
    totalOrlokIncome: number;
    bank?: string;
    accountNumber?: string;
    accountName?: string;
  }>;
  totalPayments: number;
  totalAmount: number;
  totalWimaxIncome: number;
  totalOrpusIncome: number;
  totalOrdaIncome: number;
  totalOrlokIncome: number;
}

interface TransferPlan {
  _id: string;
  batchName: string;
  planId: string;
}

export function TransferPlanLocationPage() {
  const [, params] = useRoute<{ id: string }>("/keuangan/transfer-plans/:id/location");
  const [plan, setPlan] = React.useState<TransferPlan | null>(null);
  const [locationAggregation, setLocationAggregation] = React.useState<LocationAggregation | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const planId = params?.id;

  React.useEffect(() => {
    if (!planId) {
      setError("Transfer plan ID not found");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch plan details and location aggregation in parallel
        const [planData, aggregation] = await Promise.all([
          transferPlanService.getTransferPlanById(planId),
          transferPlanService.getLocationAggregation(planId),
        ]);

        setPlan(planData);
        setLocationAggregation(aggregation);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch location data";
        setError(errorMessage);
        toast.error("Failed to fetch location data", {
          description: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [planId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Group cities by province
  const groupedByProvince = React.useMemo(() => {
    if (!locationAggregation) return [];

    const provinceMap = new Map<string, {
      province: string;
      cities: typeof locationAggregation.cities;
      totalCount: number;
      totalAmount: number;
      totalWimaxIncome: number;
      totalOrpusIncome: number;
      totalOrdaIncome: number;
      totalOrlokIncome: number;
      bank?: string;
      accountNumber?: string;
      accountName?: string;
    }>();

    for (const city of locationAggregation.cities) {
      if (!provinceMap.has(city.province)) {
        provinceMap.set(city.province, {
          province: city.province,
          cities: [],
          totalCount: 0,
          totalAmount: 0,
          totalWimaxIncome: 0,
          totalOrpusIncome: 0,
          totalOrdaIncome: 0,
          totalOrlokIncome: 0,
        });
      }

      const province = provinceMap.get(city.province)!;
      province.cities.push(city);
      province.totalCount += city.count;
      province.totalAmount += city.totalAmount;
      province.totalWimaxIncome += city.totalWimaxIncome;
      province.totalOrpusIncome += city.totalOrpusIncome;
      province.totalOrdaIncome += city.totalOrdaIncome;
      province.totalOrlokIncome += city.totalOrlokIncome;
    }

    return Array.from(provinceMap.values());
  }, [locationAggregation]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Loading location data...</p>
        </div>
      </div>
    );
  }

  if (error || !plan || !locationAggregation) {
    return (
      <div className="flex-1 space-y-4 p-6">
        <Link href="/keuangan/transfer-plans">
          <Button variant="outline" className="gap-2 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Transfer Plans
          </Button>
        </Link>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error || "Location data not found"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/keuangan/transfer-plans">
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <MapPin className="h-8 w-8" />
              Location Summary
            </h1>
            <p className="text-muted-foreground mt-1">
              Transfer Plan: {plan.batchName}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle>Total Summary</CardTitle>
            <CardDescription>
              Overall statistics for all locations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Total Payments</div>
                <div className="text-lg font-semibold">{locationAggregation.totalPayments}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Amount</div>
                <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalAmount)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total Wimax Income</div>
                <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalWimaxIncome)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total ORPUS Income</div>
                <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalOrpusIncome)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total ORDA Income</div>
                <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalOrdaIncome)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total ORLOK Income</div>
                <div className="text-lg font-semibold">{formatCurrency(locationAggregation.totalOrlokIncome)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Province and City Breakdown Table */}
        <Card>
          <CardHeader>
            <CardTitle>Location Breakdown</CardTitle>
            <CardDescription>
              Provinsi Transfer Location Plan and Kota/ Kabupaten Location Transfer Plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            {groupedByProvince.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No location data available
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Location</TableHead>
                      <TableHead className="text-right">Total Transaksi</TableHead>
                      <TableHead className="text-right">Jumlah Akumulatif</TableHead>
                      <TableHead>Bank</TableHead>
                      <TableHead>No rekening</TableHead>
                      <TableHead>Nama pemegang rekening</TableHead>
                      <TableHead>Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedByProvince.map((provinceData, provinceIdx) => (
                      <React.Fragment key={provinceIdx}>
                        {/* Province Row */}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell className="font-semibold">
                            {provinceData.province}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {provinceData.totalCount}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(provinceData.totalOrdaIncome)}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {provinceData.bank || "-"}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {provinceData.accountNumber || "-"}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {provinceData.accountName || "-"}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {provinceData.bank && provinceData.accountNumber && provinceData.accountName ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                confirm
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                hold
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                        {/* City/Kabkot Rows */}
                        {provinceData.cities.map((city, cityIdx) => (
                          <TableRow key={cityIdx} className="bg-background">
                            <TableCell className="pl-8">
                              <span className="text-muted-foreground">└─</span> {city.kabkot}
                            </TableCell>
                            <TableCell className="text-right">{city.count}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(city.totalOrlokIncome)}
                            </TableCell>
                            <TableCell>{city.bank || "-"}</TableCell>
                            <TableCell>{city.accountNumber || "-"}</TableCell>
                            <TableCell>{city.accountName || "-"}</TableCell>
                            <TableCell>
                              {city.bank && city.accountNumber && city.accountName ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  confirm
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  hold
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                           {/* Wimax Income Row - At bottom of entire table */}
                           {locationAggregation && (
                             <TableRow className="bg-muted/30 font-semibold">
                               <TableCell className="font-semibold">
                                 Wimax Income
                               </TableCell>
                               <TableCell className="text-right">-</TableCell>
                               <TableCell className="text-right font-semibold">
                                 {formatCurrency(locationAggregation.totalWimaxIncome)}
                               </TableCell>
                               <TableCell>-</TableCell>
                               <TableCell>-</TableCell>
                               <TableCell>-</TableCell>
                               <TableCell>-</TableCell>
                             </TableRow>
                           )}
                           {/* ORPUS Income Row - At bottom of entire table */}
                           {locationAggregation && (
                             <TableRow className="bg-muted/30 font-semibold">
                               <TableCell className="font-semibold">
                                 ORPUS Income
                               </TableCell>
                               <TableCell className="text-right">-</TableCell>
                               <TableCell className="text-right font-semibold">
                                 {formatCurrency(locationAggregation.totalOrpusIncome)}
                               </TableCell>
                               <TableCell>-</TableCell>
                               <TableCell>-</TableCell>
                               <TableCell>-</TableCell>
                               <TableCell>-</TableCell>
                             </TableRow>
                           )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

